// ClassificaScreen.tsx - League standings with native components (no WebView)

import React, {useState, useEffect, useCallback} from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  FlatList,
  Image,
  RefreshControl,
} from 'react-native';
import {NativeStackNavigationProp} from '@react-navigation/native-stack';
import {useTheme} from '../hooks/useTheme';
import {Header} from '../components/Header';
import {Colors} from '../theme/colors';
import {API_CONFIG} from '../services/config';

type RootStackParamList = {
  Home: undefined;
  Classifica: undefined;
};

type ClassificaScreenProps = {
  navigation: NativeStackNavigationProp<RootStackParamList, 'Classifica'>;
};

type TabType = 'seriea' | 'primavera' | 'femminile';

interface Standing {
  rank: number;
  team_id: number;
  team_name: string;
  short_name: string;
  crest_url: string;
  points: number;
  played: number;
  won: number;
  draw: number;
  lost: number;
  goal_difference: number;
  form: string | null;
}

const TABS: {key: TabType; label: string}[] = [
  {key: 'seriea', label: 'Serie A'},
  {key: 'primavera', label: 'Primavera'},
  {key: 'femminile', label: 'Femminile'},
];

// Badge colors based on rank and league
const getRankBadgeStyle = (rank: number, league: TabType) => {
  if (league === 'primavera') {
    // Primavera: 1-2 Semi finali (dark purple), 3-6 Quarti (light purple), 17-18 Playout (orange), 19-20 Retrocessione (red)
    if (rank <= 2) return {backgroundColor: '#6b21a8'}; // Dark purple
    if (rank <= 6) return {backgroundColor: '#9b7dbb'}; // Light purple
    if (rank >= 19) return {backgroundColor: '#991b1b'};
    if (rank >= 17) return {backgroundColor: '#ef4444'};
    return {backgroundColor: '#e5e7eb'};
  }
  
  if (league === 'femminile') {
    // Femminile: 1-2 Champions League (dark purple), 11-12 Serie B Femminile (red)
    if (rank <= 2) return {backgroundColor: '#6b21a8'}; // Dark purple
    if (rank >= 11) return {backgroundColor: '#ef4444'};
    return {backgroundColor: '#e5e7eb'};
  }
  
  // Serie A (default)
  if (rank <= 4) return {backgroundColor: '#6b21a8'}; // Champions League - Dark purple
  if (rank <= 6) return {backgroundColor: '#9b7dbb'}; // Europa/Conference - Light purple
  if (rank >= 18) return {backgroundColor: '#ef4444'}; // Relegation - Red
  return {backgroundColor: '#e5e7eb'}; // Default - Gray
};

const getRankTextColor = (rank: number, league: TabType) => {
  if (league === 'primavera') {
    if (rank <= 6 || rank >= 17) return '#ffffff';
    return '#374151';
  }
  if (league === 'femminile') {
    if (rank <= 2 || rank >= 11) return '#ffffff';
    return '#374151';
  }
  // Serie A
  if (rank <= 6 || rank >= 18) return '#ffffff';
  return '#374151';
};

export const ClassificaScreen: React.FC<ClassificaScreenProps> = ({navigation: _navigation}) => {
  const [activeTab, setActiveTab] = useState<TabType>('seriea');
  const [standings, setStandings] = useState<Standing[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const {colors, isDark} = useTheme();

  const fetchStandings = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const response = await fetch(`${API_CONFIG.API_URL}/app/standings/${activeTab}`);
      if (!response.ok) {
        throw new Error('Failed to fetch standings');
      }
      const data = await response.json();
      setStandings(data.standings || []);
    } catch (err) {
      console.error('Error fetching standings:', err);
      setError('Impossibile caricare la classifica');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchStandings();
  }, [fetchStandings]);

  const renderStandingRow = ({item}: {item: Standing}) => {
    const isFiorentina = item.short_name.toLowerCase().includes('fiorentina');
    const rankBadgeStyle = getRankBadgeStyle(item.rank, activeTab);
    const rankTextColor = getRankTextColor(item.rank, activeTab);

    return (
      <View
        style={[
          styles.row,
          {
            backgroundColor: isFiorentina
              ? (isDark ? 'rgba(138, 43, 226, 0.3)' : 'rgba(138, 43, 226, 0.15)')
              : colors.surface,
            borderBottomColor: colors.border,
          },
        ]}>
        {/* Rank Badge */}
        <View style={styles.rankCell}>
          <View style={[styles.rankBadge, rankBadgeStyle]}>
            <Text style={[styles.rankText, {color: rankTextColor}]}>{item.rank}</Text>
          </View>
        </View>

        {/* Team Info */}
        <View style={styles.teamCell}>
          <Image source={{uri: item.crest_url}} style={styles.teamLogo} />
          <Text
            style={[styles.teamName, {color: colors.textPrimary}]}
            numberOfLines={1}>
            {item.short_name}
          </Text>
        </View>

        {/* Stats */}
        <Text style={[styles.statCell, styles.pointsCell, {color: colors.textPrimary}]}>
          {item.points}
        </Text>
        <Text style={[styles.statCell, {color: colors.textSecondary}]}>{item.played}</Text>
        <Text style={[styles.statCell, {color: colors.textSecondary}]}>{item.won}</Text>
        <Text style={[styles.statCell, {color: colors.textSecondary}]}>{item.draw}</Text>
        <Text style={[styles.statCell, {color: colors.textSecondary}]}>{item.lost}</Text>
        <Text style={[styles.statCell, {color: colors.textSecondary}]}>{item.goal_difference}</Text>
      </View>
    );
  };

  const renderHeader = () => (
    <View style={[styles.headerRow, {backgroundColor: Colors.primary}]}>
      <Text style={[styles.headerCell, styles.rankHeaderCell]}>#</Text>
      <Text style={[styles.headerCell, styles.teamHeaderCell]}>Squadra</Text>
      <Text style={styles.headerCell}>PT</Text>
      <Text style={styles.headerCell}>PG</Text>
      <Text style={styles.headerCell}>V</Text>
      <Text style={styles.headerCell}>N</Text>
      <Text style={styles.headerCell}>P</Text>
      <Text style={styles.headerCell}>DR</Text>
    </View>
  );

  const renderLegend = () => {
    // Different legends for different leagues
    if (activeTab === 'primavera') {
      return (
        <View style={[styles.legend, {backgroundColor: colors.surface, borderTopColor: colors.border}]}>
          <View style={styles.legendItem}>
            <View style={[styles.legendDot, {backgroundColor: '#6b21a8'}]} />
            <Text style={[styles.legendText, {color: isDark ? '#ffffff' : colors.textSecondary}]}>Play Off: Semi finali</Text>
          </View>
          <View style={styles.legendItem}>
            <View style={[styles.legendDot, {backgroundColor: '#9b7dbb'}]} />
            <Text style={[styles.legendText, {color: isDark ? '#ffffff' : colors.textSecondary}]}>Play Off: Quarti</Text>
          </View>
          <View style={styles.legendItem}>
            <View style={[styles.legendDot, {backgroundColor: '#ef4444'}]} />
            <Text style={[styles.legendText, {color: isDark ? '#ffffff' : colors.textSecondary}]}>Play Out</Text>
          </View>
          <View style={styles.legendItem}>
            <View style={[styles.legendDot, {backgroundColor: '#991b1b'}]} />
            <Text style={[styles.legendText, {color: isDark ? '#ffffff' : colors.textSecondary}]}>Retrocessione</Text>
          </View>
        </View>
      );
    }
    
    if (activeTab === 'femminile') {
      return (
        <View style={[styles.legend, {backgroundColor: colors.surface, borderTopColor: colors.border}]}>
          <View style={styles.legendItem}>
            <View style={[styles.legendDot, {backgroundColor: '#6b21a8'}]} />
            <Text style={[styles.legendText, {color: isDark ? '#ffffff' : colors.textSecondary}]}>Champions League</Text>
          </View>
          <View style={styles.legendItem}>
            <View style={[styles.legendDot, {backgroundColor: '#ef4444'}]} />
            <Text style={[styles.legendText, {color: isDark ? '#ffffff' : colors.textSecondary}]}>Serie B Femminile</Text>
          </View>
        </View>
      );
    }
    
    // Serie A (default)
    return (
      <View style={[styles.legend, {backgroundColor: colors.surface, borderTopColor: colors.border}]}>
        <View style={styles.legendItem}>
          <View style={[styles.legendDot, {backgroundColor: '#6b21a8'}]} />
          <Text style={[styles.legendText, {color: isDark ? '#ffffff' : colors.textSecondary}]}>Champions League</Text>
        </View>
        <View style={styles.legendItem}>
          <View style={[styles.legendDot, {backgroundColor: '#9b7dbb'}]} />
          <Text style={[styles.legendText, {color: isDark ? '#ffffff' : colors.textSecondary}]}>Europa/Conf. League</Text>
        </View>
        <View style={styles.legendItem}>
          <View style={[styles.legendDot, {backgroundColor: '#ef4444'}]} />
          <Text style={[styles.legendText, {color: isDark ? '#ffffff' : colors.textSecondary}]}>Retrocessione</Text>
        </View>
      </View>
    );
  };

  return (
    <View style={[styles.container, {backgroundColor: colors.background}]}>
      <Header />

      {/* Tab Bar */}
      <View style={[styles.tabBar, {backgroundColor: colors.surface, borderBottomColor: colors.border}]}>
        {TABS.map((tab) => (
          <TouchableOpacity
            key={tab.key}
            style={[
              styles.tab,
              activeTab === tab.key && styles.activeTab,
            ]}
            onPress={() => setActiveTab(tab.key)}>
            <Text
              style={[
                styles.tabText,
                {color: activeTab === tab.key 
                  ? (isDark ? '#ffffff' : Colors.primary)
                  : (isDark ? '#888888' : colors.textSecondary)},
              ]}>
              {tab.label}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Content */}
      {loading && !refreshing ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : error ? (
        <View style={styles.errorContainer}>
          <Text style={[styles.errorText, {color: colors.textSecondary}]}>{error}</Text>
          <TouchableOpacity style={styles.retryButton} onPress={() => fetchStandings()}>
            <Text style={styles.retryText}>Riprova</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <FlatList
          data={standings}
          keyExtractor={(item) => item.team_id.toString()}
          renderItem={renderStandingRow}
          ListHeaderComponent={renderHeader}
          ListFooterComponent={renderLegend}
          stickyHeaderIndices={[0]}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{paddingBottom: 100, backgroundColor: colors.surface}}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={() => fetchStandings(true)}
              tintColor={colors.primary}
            />
          }
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  tabBar: {
    flexDirection: 'row',
    borderBottomWidth: 1,
  },
  tab: {
    flex: 1,
    paddingVertical: 14,
    alignItems: 'center',
    borderBottomWidth: 3,
    borderBottomColor: 'transparent',
  },
  activeTab: {
    borderBottomColor: '#9b7dbb',
  },
  tabText: {
    fontSize: 14,
    fontWeight: '600',
  },
  activeTabText: {
    color: Colors.primary,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  errorText: {
    fontSize: 16,
    marginBottom: 16,
  },
  retryButton: {
    backgroundColor: Colors.primary,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  retryText: {
    color: '#fff',
    fontWeight: '600',
  },
  // Table Styles
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 8,
  },
  headerCell: {
    color: '#fff',
    fontSize: 11,
    fontWeight: '700',
    textAlign: 'center',
    width: 32,
    textTransform: 'uppercase',
  },
  rankHeaderCell: {
    width: 40,
  },
  teamHeaderCell: {
    flex: 1,
    textAlign: 'left',
    paddingLeft: 8,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
  },
  rankCell: {
    width: 40,
    alignItems: 'center',
  },
  rankBadge: {
    width: 26,
    height: 26,
    borderRadius: 13,
    justifyContent: 'center',
    alignItems: 'center',
  },
  rankText: {
    fontSize: 12,
    fontWeight: '700',
  },
  teamCell: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: 8,
  },
  teamLogo: {
    width: 24,
    height: 24,
    marginRight: 10,
    resizeMode: 'contain',
  },
  teamName: {
    fontSize: 14,
    fontWeight: '500',
    flex: 1,
  },
  statCell: {
    width: 32,
    textAlign: 'center',
    fontSize: 13,
  },
  pointsCell: {
    fontWeight: '700',
  },
  // Legend
  legend: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    padding: 16,
    gap: 16,
    borderTopWidth: 1,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  legendDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  legendText: {
    fontSize: 11,
  },
});

export default ClassificaScreen;
